import {
  FPNMinimalNode,
  ProductHierarchyMinimalDetails,
  RPNMinimalNode,
} from 'src/types/productLines/productHierarchy.types';
import { buildFpnPath, buildProductLinePath, buildRpnPath } from './manageProgramsPathUtils';

/**
 * Sort items by sortDate in descending order (newest first)
 * @param items - Array of items with a sortDate property
 * @returns Sorted array (mutates original array)
 */
const sortByDateDesc = <T extends { sortDate: string }>(items: T[]): T[] =>
  items.sort((a, b) => new Date(b.sortDate).getTime() - new Date(a.sortDate).getTime());

export interface LastEditedProductLine {
  productLine: ProductHierarchyMinimalDetails;
  sortDate: string;
}

export interface LastEditedRPN {
  rpn: RPNMinimalNode;
  productLine: ProductHierarchyMinimalDetails;
  sortDate: string;
}

export interface LastEditedFPN {
  fpn: FPNMinimalNode;
  rpn: RPNMinimalNode;
  productLine: ProductHierarchyMinimalDetails;
  sortDate: string;
}

/**
 * Get the most recently edited product lines from the minimal hierarchy
 * @param productHierarchy - Array of minimal product hierarchy details
 * @param limit - Maximum number of items to return (default: 5)
 * @returns Array of last edited product lines sorted by most recent first
 */
export const getLastEditedProductLines = (
  productHierarchy: ProductHierarchyMinimalDetails[],
  limit: number = 5
): LastEditedProductLine[] => {
  const items = productHierarchy.map((productLine) => ({
    productLine,
    sortDate: productLine.updated_at,
  }));
  return sortByDateDesc(items).slice(0, limit);
};

/**
 * Get the most recently edited RPNs across all product lines from the minimal hierarchy
 * @param productHierarchy - Array of minimal product hierarchy details
 * @param limit - Maximum number of items to return (default: 5)
 * @returns Array of last edited RPNs sorted by most recent first
 */
export const getLastEditedRPNs = (
  productHierarchy: ProductHierarchyMinimalDetails[],
  limit: number = 5
): LastEditedRPN[] => {
  const allRpns: LastEditedRPN[] = [];

  productHierarchy.forEach((productLine) => {
    productLine.children?.forEach((rpn) => {
      allRpns.push({
        rpn,
        productLine,
        sortDate: rpn.updated_at,
      });
    });
  });

  return sortByDateDesc(allRpns).slice(0, limit);
};

/**
 * Get the most recently edited FPNs across all product lines and RPNs from the minimal hierarchy
 * @param productHierarchy - Array of minimal product hierarchy details
 * @param limit - Maximum number of items to return (default: 5)
 * @returns Array of last edited FPNs sorted by most recent first
 */
export const getLastEditedFPNs = (
  productHierarchy: ProductHierarchyMinimalDetails[],
  limit: number = 5
): LastEditedFPN[] => {
  const allFpns: LastEditedFPN[] = [];

  productHierarchy.forEach((productLine) => {
    productLine.children?.forEach((rpn) => {
      rpn.children?.forEach((fpn) => {
        allFpns.push({
          fpn,
          rpn,
          productLine,
          sortDate: fpn.updated_at,
        });
      });
    });
  });

  return sortByDateDesc(allFpns).slice(0, limit);
};

/**
 * Get the most recently edited product lines by a specific user from the minimal hierarchy
 * @param productHierarchy - Array of minimal product hierarchy details
 * @param userAlias - User alias to filter by
 * @param limit - Maximum number of items to return (default: 5)
 * @returns Array of last edited product lines by the user sorted by most recent first
 */
export const getLastEditedProductLinesByUser = (
  productHierarchy: ProductHierarchyMinimalDetails[],
  userAlias: string,
  limit: number = 5
): LastEditedProductLine[] => {
  const items = productHierarchy
    .filter((productLine) => productLine.updated_by === userAlias)
    .map((productLine) => ({
      productLine,
      sortDate: productLine.updated_at,
    }));
  return sortByDateDesc(items).slice(0, limit);
};

/**
 * Get the most recently edited RPNs by a specific user from the minimal hierarchy
 * @param productHierarchy - Array of minimal product hierarchy details
 * @param userAlias - User alias to filter by
 * @param limit - Maximum number of items to return (default: 5)
 * @returns Array of last edited RPNs by the user sorted by most recent first
 */
export const getLastEditedRPNsByUser = (
  productHierarchy: ProductHierarchyMinimalDetails[],
  userAlias: string,
  limit: number = 5
): LastEditedRPN[] => {
  const allRpns: LastEditedRPN[] = [];

  productHierarchy.forEach((productLine) => {
    productLine.children?.forEach((rpn) => {
      if (rpn.updated_by === userAlias) {
        allRpns.push({
          rpn,
          productLine,
          sortDate: rpn.updated_at,
        });
      }
    });
  });

  return sortByDateDesc(allRpns).slice(0, limit);
};

/**
 * Get the most recently edited FPNs by a specific user from the minimal hierarchy
 * @param productHierarchy - Array of minimal product hierarchy details
 * @param userAlias - User alias to filter by
 * @param limit - Maximum number of items to return (default: 5)
 * @returns Array of last edited FPNs by the user sorted by most recent first
 */
export const getLastEditedFPNsByUser = (
  productHierarchy: ProductHierarchyMinimalDetails[],
  userAlias: string,
  limit: number = 5
): LastEditedFPN[] => {
  const allFpns: LastEditedFPN[] = [];

  productHierarchy.forEach((productLine) => {
    productLine.children?.forEach((rpn) => {
      rpn.children?.forEach((fpn) => {
        if (fpn.updated_by === userAlias) {
          allFpns.push({
            fpn,
            rpn,
            productLine,
            sortDate: fpn.updated_at,
          });
        }
      });
    });
  });

  return sortByDateDesc(allFpns).slice(0, limit);
};

/**
 * Get the first available product line from the minimal hierarchy
 * @param productHierarchy - Array of minimal product hierarchy details
 * @returns The first product line or null if none available
 */
export const getFirstAvailableProductLine = (
  productHierarchy: ProductHierarchyMinimalDetails[]
): ProductHierarchyMinimalDetails | null => {
  if (productHierarchy.length === 0) {
    return null;
  }
  return productHierarchy[0];
};

/**
 * Result type for program redirect path resolution
 */
export interface ProgramRedirectResult {
  path: string;
  type: 'fpn' | 'rpn' | 'product-line';
  source: 'user-edited' | 'global-edited' | 'first-available';
}

/**
 * Get the best redirect path for View/Edit Programs navigation using minimal hierarchy data.
 *
 * Priority logic:
 * 1. Current user's last edited FPN
 * 2. Current user's last edited RPN
 * 3. Current user's last edited Product Line
 * 4. Global last edited FPN
 * 5. Global last edited RPN
 * 6. Global last edited Product Line
 * 7. First available product line
 *
 * @param productHierarchy - Array of minimal product hierarchy details
 * @param currentUserAlias - Current user's alias (optional)
 * @returns ProgramRedirectResult with path, type, and source, or null if no data available
 */
export const getBestProgramRedirectPath = (
  productHierarchy: ProductHierarchyMinimalDetails[],
  currentUserAlias?: string
): ProgramRedirectResult | null => {
  if (!productHierarchy || productHierarchy.length === 0) {
    return null;
  }

  // Priority 1-3: Current user's last edited items
  if (currentUserAlias) {
    // Check user's FPNs first
    const userFpns = getLastEditedFPNsByUser(productHierarchy, currentUserAlias, 1);
    if (userFpns.length > 0) {
      const { fpn, rpn, productLine } = userFpns[0];
      return {
        path: buildFpnPath(productLine.product_code, rpn.rpn_display_id, fpn.fpn_display_id),
        type: 'fpn',
        source: 'user-edited',
      };
    }

    // Check user's RPNs
    const userRpns = getLastEditedRPNsByUser(productHierarchy, currentUserAlias, 1);
    if (userRpns.length > 0) {
      const { rpn, productLine } = userRpns[0];
      return {
        path: buildRpnPath(productLine.product_code, rpn.rpn_display_id),
        type: 'rpn',
        source: 'user-edited',
      };
    }

    // Check user's Product Lines
    const userProductLines = getLastEditedProductLinesByUser(productHierarchy, currentUserAlias, 1);
    if (userProductLines.length > 0) {
      const { productLine } = userProductLines[0];
      return {
        path: buildProductLinePath(productLine.product_code),
        type: 'product-line',
        source: 'user-edited',
      };
    }
  }

  // Priority 4-6: Global last edited items
  // Check global FPNs
  const globalFpns = getLastEditedFPNs(productHierarchy, 1);
  if (globalFpns.length > 0) {
    const { fpn, rpn, productLine } = globalFpns[0];
    return {
      path: buildFpnPath(productLine.product_code, rpn.rpn_display_id, fpn.fpn_display_id),
      type: 'fpn',
      source: 'global-edited',
    };
  }

  // Check global RPNs
  const globalRpns = getLastEditedRPNs(productHierarchy, 1);
  if (globalRpns.length > 0) {
    const { rpn, productLine } = globalRpns[0];
    return {
      path: buildRpnPath(productLine.product_code, rpn.rpn_display_id),
      type: 'rpn',
      source: 'global-edited',
    };
  }

  // Check global Product Lines
  const globalProductLines = getLastEditedProductLines(productHierarchy, 1);
  if (globalProductLines.length > 0) {
    const { productLine } = globalProductLines[0];
    return {
      path: buildProductLinePath(productLine.product_code),
      type: 'product-line',
      source: 'global-edited',
    };
  }

  // Priority 7: First available product line
  const firstProductLine = getFirstAvailableProductLine(productHierarchy);
  if (firstProductLine) {
    return {
      path: buildProductLinePath(firstProductLine.product_code),
      type: 'product-line',
      source: 'first-available',
    };
  }

  return null;
};
